import { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Navigate, useLocation, useParams } from 'react-router-dom';
import cx from 'classnames';
import parse from 'html-react-parser';
import { Popover } from 'antd';
import filecheckSrc from '@/assets/icons/filepcheck.svg';
import { useModal } from '@/hooks/useModal';
import {
  AboutRiskScoreModal,
  AlertBox,
  Button,
  CopyText,
  CryptoLabelPill,
  MonitoringSubscribe,
  RiskIndicator,
  RiskIndicatorLimited,
  RiskIndicatorBlackList,
  SvgIcon,
  RiskSignalDetails,
} from '@/components';
import { AmlHistoryItemData } from '@/redux/models';
import {
  getIconByNetwork,
  getRiskTitleKeyByValue,
  getRiskTypeByValue,
  getShortAddress,
  getShortHash,
  isFlowLimited,
} from '@/helpers';
import { BlackConnectionEnum, ChainsDictionary, RiskKeys } from '@/config';
import { useTranslation } from 'react-i18next';
import { amlCheckActions, amlHistoryActions } from '@/redux/actions';
import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import {
  amlHistorySelectors,
  serviceOptionsSelectors,
  userSelectors,
  profileSelectors,
  externalServiceSelectors,
} from '@/redux/selectors';
import {
  AccountModesEnum,
  AmlStatusEnum,
  CheckOutputTypeEnum,
  GetAmlHistoryItemRequest,
  SideEnum,
  AmlFlowEnum,
} from '@/api';
import useDevice from '@/hooks/useDevice';
import { CompareReportsModal } from '@/components/_modals';
import { RiskDetailsUnlock } from '@/components/RiskDetailsUnlock';
import { Loader } from '@/components/Loader';

import {
  AddressInfo,
  CheckMadeOn,
  PromoFlow,
  SanctionNote,
  TransactionInfo,
} from './components';

import styles from './AmlCheckDetails.module.sass';

interface AmlCheckDetailsProps {}

const AmlCheckDetails: FC<AmlCheckDetailsProps> = () => {
  const { isMobile } = useDevice();
  const [showAboutRiskScoreModal, setShowAboutRiskScoreModal] = useState(false);
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const location = useLocation();
  const { uid: uidParam } = useParams<{ uid?: string }>();
  const stateItem = location?.state?.item as AmlHistoryItemData;
  const stateUid = location?.state?.uid as string | undefined;
  const theInterval = useRef<any>(null);
  const { data: userData } = useAppSelector(userSelectors.getUserSlice);
  const {
    error,
    data: amlHistoryData,
    detailsItem,
    detailsLoading,
    detailsError,
  } = useAppSelector(amlHistorySelectors.getAmlHistorySlice);

  const { data: serviceOptions } = useAppSelector(
    serviceOptionsSelectors.getServiceOptionsSelectors,
  );
  const monitoringBonusCount = useAppSelector(
    profileSelectors.monitoringBonusCount,
  );
  const { data: profileData } = useAppSelector(
    profileSelectors.getProfileSlice,
  );
  const helpcrunchLoaded = useAppSelector(
    externalServiceSelectors.helpcrunchLoaded,
  );

  const [item, setItem] = useState<AmlHistoryItemData>(stateItem);
  const isPending = item?.status === AmlStatusEnum.PENDING;
  const isPromo = userData?.promoBalance;
  const madeInLimitedMode = isFlowLimited(item?.flow);
  const isInstant = item?.flow === AmlFlowEnum.INSTANT;
  const hasSignals = (item?.signalTags?.length ?? 0) > 0 || !!item?.hasHighRisk;

  const monitoringAllowed = Boolean(
    !isPending &&
      !item?.tx &&
      monitoringBonusCount &&
      monitoringBonusCount > 0 &&
      !madeInLimitedMode,
  );

  const {
    isOpen: isOpenCompareReportsModal,
    onOpenModal: handleOpenCompareReportsModal,
    onCloseModal: handleCloseCompareReportsModal,
  } = useModal();

  const performRecheck = useCallback(() => {
    if (item && !item.devMode) {
      const payload: GetAmlHistoryItemRequest = { uid: item.uuid };
      dispatch(amlHistoryActions.fetchHistoryItem(payload));
    }
  }, [item, dispatch]);

  useEffect(() => {
    if (stateItem) {
      performRecheck();
    }
  }, []);

  // opened via /details/:uid or navigate({state:{uid}}) without a router-state item
  useEffect(() => {
    const uid = uidParam || stateUid;
    if (!stateItem && uid) {
      dispatch(amlHistoryActions.fetchDetailsByUid({ uid }));
    }

    return () => {
      dispatch(amlHistoryActions.clearDetailsItem());
    };
  }, [stateItem, uidParam, stateUid, dispatch]);

  useEffect(() => {
    if (!stateItem && detailsItem) {
      setItem(detailsItem);
    }
  }, [stateItem, detailsItem]);

  useEffect(() => {
    const shouldRecheck = isPending;

    if (shouldRecheck) {
      theInterval.current = setInterval(performRecheck, 15000);
    } else {
      clearInterval(theInterval.current);
      theInterval.current = null;
    }

    return () => clearInterval(theInterval.current);
  }, [item]);

  useEffect(() => {
    if (!stateItem) return;

    const currentAmlCheck = amlHistoryData?.items.find(
      (entry) => entry.uuid === stateItem.uuid,
    );
    if (!!currentAmlCheck) {
      setItem(currentAmlCheck);
    }
  }, [stateItem, amlHistoryData?.items]);

  const blackConnectionNote = useMemo(() => {
    if (!item?.blackListsConnections) return '';

    switch (item.blackListsConnections) {
      case BlackConnectionEnum.OUT_INTERACTION:
        return t(
          `translation.black_list_${item.itemKindShort}_out_interaction_msg`,
        );
      case BlackConnectionEnum.OUT:
        if (item.status === AmlStatusEnum.SUCCESS) {
          return t(`translation.black_list_${item.itemKindShort}_out_msg`);
        }
        return '';
      case BlackConnectionEnum.IN:
        return t(`translation.black_list_${item.itemKindShort}_in_msg`);
      default:
        if ([AmlFlowEnum.INSTANT, AmlFlowEnum.LITE].includes(item.flow)) {
          return item.tx
            ? t(`translation.black_list_addr_out_interaction_msg`)
            : t(`translation.black_list_addr_out_msg`);
        }
        return '';
    }
  }, [item, t]);

  const darkMarketNote = useMemo(() => {
    const darkMarketPresent =
      Number(item?.signals ? item.signals[RiskKeys.DARK_MARKET] : 0) > 0;

    if (!darkMarketPresent) {
      return '';
    }

    if (!!item?.tx) {
      return item.direction === SideEnum.DEPOSIT
        ? t('translation.dark_market_mark_tx_deposit_note')
        : t('translation.dark_market_mark_tx_withdrawal_note');
    }

    return t('translation.dark_market_mark_address_note');
  }, [item, t]);

  const signalsPresent = useMemo(() => {
    if (!item?.signals && !item?.signalTags) {
      return false;
    }

    return true;
  }, [item?.signals, item?.signalTags]);

  if (!stateItem && !uidParam && !stateUid) {
    return <Navigate to="/" />;
  }

  if (!item) {
    if (detailsError) {
      return <Navigate to="/" />;
    }
    return <Loader />;
  }

  const hasRiskScore = item.riskscore !== null;
  const riskValue = item.riskscore || 0;
  const riskType = getRiskTypeByValue(riskValue);
  const riskTitleKey = getRiskTitleKeyByValue(riskValue);
  const riskScoreLevel = item.riskScoreLevel || null;
  const networkIcon = getIconByNetwork(item.network || '');
  const networkName =
    ChainsDictionary[item.network || '']?.name || item.network;
  const assetSymbol = !!item.tx ? item.tokenDetails?.code || item.asset : '';
  const protocol = '';
  const depositNoteRequired =
    hasRiskScore &&
    item.direction === SideEnum.DEPOSIT &&
    (item.type || 0) !== CheckOutputTypeEnum.TX_APPROXIMATE;
  const approxNoteRequired =
    (hasRiskScore ||
      (item.signals &&
        Object.values(item.signals).filter((val) => Number(val) > 0).length)) &&
    (item.type || 0) === CheckOutputTypeEnum.TX_APPROXIMATE;

  const canDownloadPDFReport =
    profileData &&
    profileData.accountMode !== AccountModesEnum.LITE &&
    !item.devMode;

  const checkHandler = () => {
    if (item.uuid) {
      dispatch(amlCheckActions.checkAgain(item));
    }
  };

  const handleDownloadReport = () => {
    window.open(item.pdfReport, '_blank');
  };

  const checkTitle = item.tx
    ? t('translation.details_tx_result_title')
    : t('translation.details_address_result_title');

  return (
    <>
      <div className={styles.result}>
        <div className={styles.main}>
          <div className={styles.main__top}>
            <div className={styles.main__title}>{checkTitle}</div>

            <div className={styles.main__crypto}>
              <CryptoLabelPill
                iconUrl={networkIcon}
                label={networkName || ''}
                protocol={protocol}
                abbr={assetSymbol || ''}
              />

              <span>
                {item.tx && <span className={styles.txNote}>TxID:</span>}
                &nbsp;
                <CopyText
                  text={
                    item.tx
                      ? getShortHash(item.tx)
                      : getShortAddress(item.address || '')
                  }
                  copyText={item.tx || item.address}
                  className={styles.main__address}
                />
              </span>
            </div>

            {!blackConnectionNote && !isPending && (
              <div className={styles.blacklistFree}>
                <img src={filecheckSrc} alt="" />
                {t('translation.blacklist_free')}
              </div>
            )}

            {!!blackConnectionNote && !isPending && (
              <RiskIndicatorBlackList
                note={blackConnectionNote}
                className={styles.risk__indicator}
                amlFlow={item.flow}
              />
            )}
            {item.devMode && (
              <div className={styles.devModeTitle}>
                {t('translation.dev_mode')}
              </div>
            )}
          </div>

          <div className={styles.risk}>
            {!blackConnectionNote && !isPending && (
              <>
                {hasRiskScore && (
                  <>
                    {item.tx && (
                      <div className={styles.risk__counterparty_note}>
                        {t('translation.amlhistory_table_status')}
                      </div>
                    )}

                    <RiskIndicator
                      value={riskValue}
                      isChecking={isPending}
                      className={styles.risk__indicator}
                    />

                    <div
                      className={cx(
                        styles.risk__title,
                        styles[`risk__title--${riskType}`],
                      )}
                    >
                      {t(`translation.risk_explanation_level_${riskTitleKey}`)}
                    </div>
                  </>
                )}

                {riskScoreLevel && (
                  <RiskIndicatorLimited
                    isChecking={isPending}
                    value={riskScoreLevel}
                    amlFlow={item.flow}
                  />
                )}

                {!hasRiskScore && !riskScoreLevel && (
                  <div className={styles.risk__title}>
                    {t(
                      item.tx
                        ? 'translation.tpl_aml_no_score_tx'
                        : 'translation.tpl_aml_no_score',
                    )}
                  </div>
                )}
              </>
            )}

            {isPending && (
              <>
                <RiskIndicator
                  value={riskValue}
                  className={styles.risk__indicator}
                  amlFlow={item.flow}
                  isChecking
                />

                <div className={styles.checking}>
                  {t('translation.pending_status_hint')}
                </div>
              </>
            )}

            {!!darkMarketNote && (
              <div className={styles.risk__description}>{darkMarketNote}</div>
            )}

            {!blackConnectionNote && !riskScoreLevel && (
              <Button
                size="m"
                type="secondary"
                onClick={() => setShowAboutRiskScoreModal(true)}
              >
                {t('translation.riskscore_more_btn')}
              </Button>
            )}
            {serviceOptions?.limitFlowAssets.includes(item.network) && (
              <AlertBox
                type="warning"
                className={styles.message}
                iconPlacement="start"
              >
                {{
                  title: t('translation.limited_coins_info_title'),
                  description: parse(t('translation.limited_coins_info_desc')),
                  icon: '☝',
                }}
              </AlertBox>
            )}
          </div>

          {!isPromo && (
            <>
              {isPending && (
                <div className={styles.signalsSkeleton}>
                  <div className={styles.skeletonWrap}>
                    <div className={styles.skeletonTitle} />
                    <div className={styles.skeletonLine} />
                    <div className={styles.skeletonLine} />
                    <div className={styles.skeletonLine} />
                  </div>

                  <div className={styles.skeletonWrap}>
                    <div className={styles.skeletonTitle} />
                    <div className={styles.skeletonLine} />
                    <div className={styles.skeletonLine} />
                  </div>

                  <div className={styles.skeletonWrap}>
                    <div className={styles.skeletonTitle} />
                    <div className={styles.skeletonLine} />
                  </div>
                </div>
              )}

              {signalsPresent && !isPending && (
                <RiskSignalDetails
                  isLimitedMode={madeInLimitedMode}
                  hasSignals={hasSignals}
                  item={item}
                />
              )}

              {(!!depositNoteRequired || !!approxNoteRequired) && (
                <div className={styles.details__notes}>
                  {depositNoteRequired &&
                    t('translation.tx_check_deposit_note')}
                  {approxNoteRequired && t('translation.tx_approx_check_note')}
                </div>
              )}
            </>
          )}
          {isPromo && <PromoFlow />}

          {madeInLimitedMode && (
            <RiskDetailsUnlock
              className={hasSignals ? styles.unlock : undefined}
            />
          )}
        </div>

        <div className={styles.sidebar}>
          <div className={styles.sidebarInner}>
            <CheckMadeOn
              item={item}
              isPromo={isPromo}
              isLoading={isPending}
              checkAgainHandler={checkHandler}
            />

            <div className={styles.details__actions}>
              {canDownloadPDFReport && (
                <Button
                  type="secondary"
                  size="m"
                  disabled={isPending}
                  onClick={handleDownloadReport}
                  minWidth={260}
                  corner="rect"
                  nowrap
                >
                  <SvgIcon name="pdf" />
                  {t('translation.download_pdf_btn')}
                </Button>
              )}

              {!!monitoringAllowed &&
                !isFlowLimited(item.flow) &&
                !isInstant && (
                  <div className={styles.details__actionsGroup}>
                    <MonitoringSubscribe item={item}>
                      {(openConfirmModal: any) => (
                        <Popover
                          zIndex={9999}
                          content={t('translation.monitoring_bonus_left', {
                            count: monitoringBonusCount,
                          })}
                        >
                          <div className={styles.details__actionsButton}>
                            <Button
                              type="secondary"
                              size={isMobile ? 'm' : 's'}
                              onClick={openConfirmModal}
                              fullwidth
                            >
                              <SvgIcon name="monitoring" />
                              {t('translation.subscribe_btn')}
                              <div className={styles.subscribeBadge}>
                                {monitoringBonusCount}
                              </div>
                            </Button>
                          </div>
                        </Popover>
                      )}
                    </MonitoringSubscribe>
                  </div>
                )}
            </div>

            <SanctionNote item={item} isPromo={isPromo} isLoading={isPending} />

            {item.tx && <TransactionInfo item={item} isLoading={isPending} />}
            {!item.tx && <AddressInfo item={item} isLoading={isPending} />}
          </div>
        </div>
      </div>
      <AboutRiskScoreModal
        isOpen={showAboutRiskScoreModal}
        onClose={() => setShowAboutRiskScoreModal(false)}
      />
      <CompareReportsModal
        isOpen={isOpenCompareReportsModal}
        onClose={handleCloseCompareReportsModal}
      />
    </>
  );
};

export default AmlCheckDetails;
